import type { TemplateIconName } from './EditorCharts';
import type { ImageMedia } from './Media';
import type { IOrganizationData } from './Organization';
import type { RecordingTypes } from './Recording';
import type { IUser } from './User';

export type RecordingType = 'overlay' | 'cloner';

export enum IEditorStatus {
  editorLoaded = 'editor-loaded',
  recorderLoaded = 'recorder-loaded',
}

export enum IActionType {
  startup = 'startup',
  startEditorMode = 'start-editor-mode',
  startPreviewMode = 'start-preview-mode',
  edits = 'edits',
  setEditMode = 'set-edit-mode',
  startPreview = 'start-preview',
  undoElement = 'undo-element',
  redoElement = 'redo-element',
  feedNestedIframe = 'feed-nested-iframe',
  saveEditInParentIframe = 'save-edit-in-parent-iframe',
  setPresentationDomain = 'set-presentation-domain',
  reload = 'reload',
  restoreEdit = 'restore-edit',
  resetEdits = 'reset-edits',
  startLoader = 'start-loader',
  stopLoader = 'stop-loader',
  doneEditing = 'done-editing',
  setUserData = 'set-user-data',
  injectMetaTag = 'inject-meta-tag',
  closeToast = 'close-toast',
  setNavigateMode = 'set-navigate-mode',
  sendingMetadata = 'sending-metadata',
  sendMetadata = 'send-metadata',
  setChartsTemplates = 'set-charts-templates',
  getChartsTemplates = 'get-charts-templates',
  setVariables = 'set-variables',
  destroyHoverHighlighter = 'destroyHoverHighlighter',
  destroySelectedHighlighter = 'destroySelectedHighlighter',
  setFreezeMode = 'setFreezeMode',
  navigateBack = 'navigateBack',
  navigateForward = 'navigateForward',
  createRuleRequest = 'create-rule-request',
  generateAiRequest = 'generate-ai-request',
  createRuleResponse = 'create-rule-response',
  startInspectElements = 'start-inspect-elements',
  stopInspectElements = 'stop-inspect-elements',
  inspectedElement = 'inspected-element',
  updateCurrentPageUrl = 'update-current-page-url',
  openUrlLink = 'open-url-link',
  editCreated = 'edit-created',
  startFindAndReplaceMode = 'start-find-and-replace-mode',
  stopFindAndReplaceMode = 'stop-find-and-replace-mode',
  startBlockInteractionsMode = 'start-block-interactions-mode',
  stopBlockInteractionsMode = 'stop-block-interactions-mode',
  startGuidanceTooltipsMode = 'start-guidance-tooltips-mode',
  stopGuidanceTooltipsMode = 'stop-guidance-tooltips-mode',
  getMetadata = 'get-metatdata',
  editorSandboxEvents = 'editor-sandbox-events',
  reloadEditorFrame = 'reload-editor-frame',
  addTransaction = 'ds-network-transaction',
  setRules = 'set-rules',
  deleteEdits = 'delete-edits',
  notify = 'notify',
  log = 'log',
  reportAnalytics = 'report-analytics',
  setModifications = 'set-modifications',
  refreshSessionAndReload = 'refresh-session-and-reload',
  highlightTarget = 'highlight-target',
  destroyHighlightTarget = 'destroy-highlight-target',
  createDataRuleFromElement = 'create-data-rule-from-element',
  visualSearch = 'visual-search',
  navigateToTarget = 'navigate-to-target',
  updateUrl = 'update-url',
  editGuidanceModification = 'edit-guidance-modification',
  deleteGuidanceModification = 'delete-guidance-modification',
  restoreGuidanceModification = 'restore-guidance-modification',
  navigateToModification = 'navigate-to-modification',
  highlightGuidanceTooltip = 'highlight-guidance-tooltip',
  destroyHighlightGuidanceTooltip = 'destroy-highlight-guidance-tooltip',
  showAllGuidanceTooltipModifications = 'show-all-guidance-tooltip-modifications',
  hideAllGuidanceTooltipModifications = 'hide-all-guidance-tooltip-modifications',
  openGuidanceTooltip = 'open-guidance-tooltip',
  updateRuleGlobalStatus = 'update-rule-global-status',
  setIsGuidanceTooltipOpen = 'set-is-guidance-tooltip-open',
  stopIsGuidanceTooltipsEditLoading = 'stop-is-guidance-tooltips-edit-loading',
  showDemoModifiedNotification = 'show-demo-modified-notification',
  togglePerformanceMeasuring = 'toggle-performance-measuring',
  setPerformanceResults = 'set-performance-results',
  stopPerformanceMeasuring = 'stop-performance-measuring',
  startPerformancePickSelectorMode = 'start-performance-pick-selector-mode',
  stopPerformancePickSelectorMode = 'stop-performance-pick-selector-mode',
}

export enum IEditActionsType {
  navigate = 'navigate',
  inspect = 'inspect',
}

export interface IAction {
  type: IActionType;
  payload?: any;
}

export interface IDynamicEdit {
  editData: IDynamicEditData;
  draftId: number;
  id: number;
}

export type CssPickersType = Omit<Partial<CSSStyleDeclaration>, 'parentRule'>;

export interface IDynamicEditCssElement {
  isCustomCssEnabled?: boolean;
  customCss?: string;
  pickers?: CssPickersType;
  isTransparent?: boolean;
}

export interface DOMTreeRepresentation {
  representation: string;
  options: {
    ignoredNodeTypes?: number[];
  };
}

export interface IDynamicEditSerializedElement {
  serializedXml?: string;
  originalElement?: string;
  specialAttributes?: Record<string, string | number>;
  domTreeRepresentation?: DOMTreeRepresentation | string;
  moveType?: IListEditType;
  elementType?: IEditElementType;
  charts?: {
    options?: string;
    chartType?: string;
    originalElement?: string;
    chartEl?: string;
    shouldBeRenderd?: boolean;
    revertedEdit?: boolean;
    originalHCchartIndex?: number | boolean;
    chartVendor: 'ds-charts' | 'highcharts' | 'chartsjs' | 'recharts';
  };
  input?: {
    value: string;
    placeholder: string;
    disabled: boolean;
  };
  link?: {
    href: string;
    target: string;
  };
  fallbackXpath?: string;
  cssSettings?: IDynamicEditCssElement;
  originalSrc?: string;
  customCssSelector?: string;
  jsPath?: string[];
  imageSettings?: Pick<ImageMedia, 'sizing' | 'position'>;
  renameValue?: string;
  prevRuleId?: string;
  elementBoundaries?: {
    width: number;
    height: number;
  };
}

export interface IDynamicEditElement {
  xpath: string;
  value: string;
  left: number;
  top: number;
  serialized: IDynamicEditSerializedElement;
  classes: string;
}

export enum IDynamicEditType {
  text = 'text',
  chart = 'chart',
  image = 'image',
  list = 'list',
  style = 'style',
  input = 'input',
  visibility = 'visibility',
  delete = 'delete',
  lock = 'lock',
  unlock = 'unlock',
  link = 'link',
}

export interface IDynamicEditData {
  domain: string;
  url: string;
  type: IDynamicEditType;
  element: {
    old: IDynamicEditElement;
    new: IDynamicEditElement;
  };
  isVariable: boolean;
  isGlobal?: boolean;
  timestamp: number;
  reference?: string;
  recordingId?: string;
  prevTimestamp?: number;
  nextTimestamp?: number;
  applied?: boolean;
}

export interface IGuidanceTooltips {
  id?: string;
  type: GuidanceTooltipSchemaType.guidanceTooltip;
  domain?: string;
  url: string;
  title: string;
  text: string;
  timestamp: number;
  jsPath: string[];
  xpath: string;
  isParentElementDeleted: boolean;
  isGlobal?: boolean;
}

export interface IObjectEditingSettings {
  type: ObjectEditingSettingsSchemaType.objectEditingSettings;
  display_name: string;
  hide: boolean;
  filter_columns: string[];
  object_id: string;
  set_object_pk: string;
}

export interface IBlockInteractionsSettings {
  type: NavigationEditSchemaType.blockInteractionsSettings;
  isActive: boolean;
  tooltipPosition?: 'top' | 'bottom';
  tooltipText: string;
  tooltipTheme?: 'dark' | 'light';
}

export enum IListEditType {
  down = 'down',
  up = 'up',
  duplicate = 'duplicate',
  delete = 'delete',
  hide = 'hide',
}

export enum IFindAndReplaceRuleType {
  replaceText = 'replace_text',
  replaceImage = 'replace_image',
  replaceImageWithVariable = 'replace_image_with_variable',
  snippet = 'snippet',
  replaceLink = 'replace_link',
}

interface IEditRule<T extends IFindAndReplaceRuleType> {
  isGlobal: boolean; // deprecated
  type: T;
  ruleId: string;
}

export interface ISnippetRule
  extends IEditRule<IFindAndReplaceRuleType.snippet> {
  source: string;
  snippetType: SnippetType;
  name: string;
  isActive: boolean; // will be deprecated after we migrate the editor to modifications
}

export interface ITextReplaceRule
  extends IEditRule<IFindAndReplaceRuleType.replaceText> {
  options: TextReplaceOptions;
  newValue: string;
  oldValue: string;
}

export interface IImageReplaceRule
  extends IEditRule<IFindAndReplaceRuleType.replaceImage> {
  sourceUrl: string;
  sourceFileServiceLink: string;
  destinationFileServiceLink: string;
  sourceFileMetadata: ImageFileMetadata;
  destinationFileMetadata: ImageFileMetadata;
}

export interface IImageReplaceWithVariableRule
  extends IEditRule<IFindAndReplaceRuleType.replaceImageWithVariable> {
  sourceUrl: string;
  sourceFileServiceLink: string;
  destinationVariableId: ImageVariable['variableId'];
  sourceFileMetadata: ImageFileMetadata;
}

export interface ILinkReplaceRule
  extends IEditRule<IFindAndReplaceRuleType.replaceLink> {
  applyOn: RuleApplyOnType;
  replaceType: LinkEditTypes;
  findBy: LinkEditBy;
  newValue: string;
  elementData: Record<string, unknown>;
  isExternal?: boolean;
  openInNewTab?: boolean;
  oldValue: string;
}

export interface TextReplaceOptions {
  isCaseSensitive: boolean;
  isExactWord: boolean;
  applyOnUrls?: string[];
}

export interface ImageFileMetadata {
  name: string;
  format: string;
  width: number;
  height: number;
}

export type FindReplaceRule =
  | ITextReplaceRule
  | IImageReplaceRule
  | IImageReplaceWithVariableRule
  | ISnippetRule
  | ILinkReplaceRule;

export enum IRuleActionType {
  new = 'new',
  edit = 'edit',
  delete = 'delete',
  complete = 'complete',
  reorder = 'reorder',
}

export interface IRuleAction {
  action: IRuleActionType;
  rule: FindReplaceRule;
}

export enum InspectAction {
  text = 'text',
  image = 'image',
  list = 'list',
  chart = 'chart',
  container = 'container',
  input = 'input',
  link = 'link',
  button = 'button',
}

export interface IEditorUserData {
  user?: IUser;
  organizationId: string;
  organization?: IOrganizationData;
}

export enum RecorderStatus {
  READY = 'READY',
  RECORD_IN_PROGRESS = 'RECORD_IN_PROGRESS',
  URL_CHANGED = 'URL_CHANGED',
  RESET_DEMO = 'RESET_DEMO',
  EDITOR_LOADED = 'EDITOR_LOADED',
  RECORDER_RELOAD = 'RECORDER_RELOAD',
  RECORDER_LOADED = 'recorder-loaded',
}

export interface IAttribute {
  name: string;
  value: string;
}

export interface IHeadTag {
  name: string;
  attributes: IAttribute[];
}

export type HighlightMode = 'hover' | 'selected';
export type DestroyTrigger =
  | 'keydown'
  | 'keyup'
  | 'mouseleave'
  | 'onblur'
  | 'event';

export type IVariableType = 'text' | 'image';

export interface IVariable {
  variableId?: string;
  description: string;
  name: string;
  type: IVariableType;
  value: string;
}

export interface TextVariable extends IVariable {
  type: 'text';
  code: string;
  isGlobal?: boolean;
}

export interface ImageVariable extends IVariable {
  type: 'image';
  variableId: string;
  imageMetadata: ImageFileMetadata;
}

export enum RuleApplyOnType {
  allPages = 'allPages',
  currentPage = 'currentPage',
  specificElement = 'specificElement',
}

export interface BlockInteractionsRule {
  type: NavigationEditSchemaType.blockInteractions;
  applyOn: RuleApplyOnType;
  ruleId: string;
  blockingType: BlockInteractionsBlockingTypes;
  blockBy: BlockInteractionsBlockBy;
  blockingValue: string;
  elementData: Record<string, unknown>;
}

export type EditorSourceType = 'demo' | 'record';

export interface IEditorDraftEdit {
  createTime: string;
  creator: IUser;
  draftId: IEditorDraft['id'];
  editData: IDynamicEditData;
  id: number;
  isDeleted: boolean;
  updateTime: string;
}

export interface IEditorDraft {
  commands: any[];
  commits: any[];
  archiveTime: string;
  comment: string;
  createTime: string;
  creator: IUser;
  envId: number;
  firstUrl: string;
  id: number;
  image: string;
  isDeleted: boolean;
  updateTime: string;
  versionId: number;
  recordingType: RecordingTypes;
  rules: FindReplaceRule[];
  variables: IVariable[];
  modified: boolean;
  playerToken: string;
  sourceId: string;
  sourceRevisionId: number;
  sourceType: string;
  recorderUuid: string;
  revisionId: number;
  fileServiceToken: string;
}

export interface RuleAction {
  rule?: FindReplaceRule;
  action: IRuleActionType;
}

export type IListItemSelectorsTypes = 'whitelist' | 'blacklist';

export interface IListItemSelectors {
  selectors: {
    [key in IListItemSelectorsTypes]?: string[];
  };
  inputValues: {
    [key in IListItemSelectorsTypes]?: string;
  };
}

export const IListItemSelectorKeys: Array<IListItemSelectorsTypes> = [
  'whitelist',
  'blacklist',
];

export enum IPageMatchingRuleType {
  stripPath = 'stripPath',
  stripQueryParam = 'stripQueryParam',
}

export interface IStripPathAttributes {
  match: RegExp;
  strip: string;
}

export interface IStripQueryParamAttributes {
  params: string[];
}

export interface IPageMatchingRule {
  type: IPageMatchingRuleType;
  attributes: IStripPathAttributes | IStripQueryParamAttributes;
}

// React internal input element handlers
export interface IReactElementValueTracker {
  getValue: () => unknown;
  setValue: (value: string | boolean) => void;
  stopTracking: () => void;
}

export interface ICustomTemplateChart {
  templateName: string;
  isActive: boolean;
  template: string;
  templateIcon: TemplateIconName;
  id?: string;
  version?: string;
  isDeleted?: boolean;
}

export enum IEditElementType {
  image = 'Image',
  container = 'Container',
  chart = 'Chart',
  text = 'Text',
  list = 'List item',
}

export enum ImageReplacementType {
  byXpath = 'byXpath',
  bySrc = 'bySrc',
}

export enum HighlightedElementsIdsType {
  overlaySelected = 'ds-selected-element-overlay',
  overlayHover = 'ds-hover-element-overlay',
  legendSelected = 'ds-selected-element-legend',
  legendHover = 'ds-hover-element-legend',
}

export enum LegendFlippedAttrType {
  dsFlippedAttribute = 'data-ds-legend-flipped',
}

export type TInputAttributes = {
  value: string;
  placeholder: string;
  disabled: boolean;
};

// Custom code snippets
export type SnippetType = 'js' | 'css';
export type SnippetSource = 'raw' | 'url';

// Modifications
enum ModificationType {
  edit = 'edit',
  rule = 'rule',
  variable = 'variable',
  transactionRule = 'transaction_rule',
  navigationRule = 'navigation_rule',
  guidanceTooltip = 'tooltip',
  group = 'group',
}

enum EditSchemaType {
  edit = 'edit',
}

export enum GroupSchemaType {
  aiPromptGroup = 'ai_prompt_group',
}

export enum NavigationEditSchemaType {
  blockInteractions = 'block_interactions',
  replaceLink = 'replace_link',
  blockInteractionsSettings = 'block_interactions_settings',
}

export enum RuleSchemaType {
  replaceImage = 'replace_image',
  replaceText = 'replace_text',
  replaceImageWithVariable = 'replace_image_with_variable',
  snippet = 'snippet',
  replaceLink = 'replace_link',
}

enum VariableSchemaType {
  textVariable = 'variable',
  imageVariable = 'image_variable',
}

enum TransactionRuleSchemaType {
  transactionRule = 'transaction_rule',
}

export enum GuidanceTooltipSchemaType {
  guidanceTooltip = 'guidance_tooltip',
}

export enum ObjectEditingSettingsSchemaType {
  objectEditingSettings = 'object_editing_settings',
}

export type SchemaType = `${
  | GroupSchemaType
  | EditSchemaType
  | RuleSchemaType
  | NavigationEditSchemaType
  | VariableSchemaType
  | TransactionRuleSchemaType
  | GuidanceTooltipSchemaType
  | ObjectEditingSettingsSchemaType}`;

export type SchemaTypeToContent<T extends SchemaType> =
  T extends `${RuleSchemaType.replaceImage}`
    ? IImageReplaceRule
    : T extends `${RuleSchemaType.replaceText}`
      ? ITextReplaceRule
      : T extends `${RuleSchemaType.replaceImageWithVariable}`
        ? IImageReplaceWithVariableRule
        : T extends `${VariableSchemaType.textVariable}`
          ? TextVariable
          : T extends `${VariableSchemaType.imageVariable}`
            ? ImageVariable
            : T extends `${EditSchemaType.edit}`
              ? IDynamicEditData
              : T extends `${RuleSchemaType.snippet}`
                ? ISnippetRule
                : T extends `${TransactionRuleSchemaType.transactionRule}`
                  ? TransactionRule
                  : T extends `${NavigationEditSchemaType.blockInteractions}`
                    ? BlockInteractionsRule
                    : T extends `${NavigationEditSchemaType.replaceLink}`
                      ? ILinkReplaceRule
                      : T extends `${GuidanceTooltipSchemaType.guidanceTooltip}`
                        ? IGuidanceTooltips
                        : T extends `${ObjectEditingSettingsSchemaType.objectEditingSettings}`
                          ? IObjectEditingSettings
                          : T extends `${NavigationEditSchemaType.blockInteractionsSettings}`
                            ? IBlockInteractionsSettings
                            : T extends `${GroupSchemaType.aiPromptGroup}`
                              ? IAIPromptGroup
                              : unknown;

export type SchemaTypeToModificationType<T extends SchemaType> =
  T extends `${EditSchemaType}`
    ? `${ModificationType.edit}`
    : T extends `${RuleSchemaType}`
      ? `${ModificationType.rule}`
      : T extends `${VariableSchemaType}`
        ? `${ModificationType.variable}`
        : T extends `${TransactionRuleSchemaType}`
          ? `${ModificationType.transactionRule}`
          : T extends `${NavigationEditSchemaType}`
            ? `${ModificationType.navigationRule}`
            : T extends `${GroupSchemaType}`
              ? `${ModificationType.group}`
              : T extends `${GuidanceTooltipSchemaType}`
                ? `${ModificationType.guidanceTooltip}`
                : `${ModificationType}`;

export interface Modification<T extends SchemaType = any>
  extends Record<string, unknown> {
  id: string;
  selectors?: Selector[];
  organizationId: IOrganizationData['id'];
  creator: string;
  schemaType: T;
  modificationType: SchemaTypeToModificationType<T>;
  recorderUuid: string;
  isDeleted: boolean;
  isDraft: boolean;
  isActive: boolean;
  isLocked: boolean;
  order: number;
  groupId?: string;
  content: SchemaTypeToContent<T>;
  createTime: number;
}

export type Headers = Record<string, string[]>;
export type HeadersList = { name: string; value: string }[];

export type HTTPMessageType = 'request' | 'response';

export interface HTTPMessage {
  headers: Headers;
  body?: string;
}

export interface HTTPRequest extends HTTPMessage {
  method: string;
  url: string;
}

export interface HTTPResponse extends HTTPMessage {
  status: number;
}

export interface HTTPTransaction {
  request: HTTPRequest;
  response: HTTPResponse;
}

export interface ModifiedHTTPTransaction extends HTTPTransaction {
  matchedModifications: Modification['id'][];
}

export interface Transaction {
  original: HTTPTransaction;
  modified?: ModifiedHTTPTransaction;
}

export interface BasicSelectorModifier<
  Type extends string,
  Apply extends HTTPMessageType,
  Args extends Record<string, unknown>,
> {
  type: Type;
  applyTo: Apply;
  arguments: Args;
}

export type UrlMatchSelector = BasicSelectorModifier<
  'UrlMatch',
  'request',
  {
    mode: 'exact' | 'in' | 'suffix' | 'prefix' | 'regex';
    match: string;
    // URL: <scheme>://<netloc>/<path>;<params>?<query>#<fragment>
    urlPart?: 'schema' | 'netloc' | 'path' | 'params' | 'query' | 'fragment'; // if not specified, the whole URL is matched
  }
>;

export type MethodMatchSelector = BasicSelectorModifier<
  'MethodMatch',
  'request',
  {
    match: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  }
>;

export type ContentContainsSelector = BasicSelectorModifier<
  'ContentContains',
  'request' | 'response',
  {
    match: string;
    caseSensitive?: boolean; // default value is true
  }
>;

export type MatchContentSha256Selector = BasicSelectorModifier<
  'MatchContentSha256',
  'response',
  {
    match: string[];
  }
>;

export type MockResponseModifier = BasicSelectorModifier<
  'MockResponse',
  'response',
  {
    content: string;
    contentEncoding: 'base64' | 'url';
    statusCode?: number; // default value is 200
    headers?: HeadersList;
    representationType?: 'raw' | 'form';
    representationFields?: any[];
    representationFieldsUrl?: string;
  }
>;

export type ContentReplaceModifier = BasicSelectorModifier<
  'ContentReplace',
  'response',
  {
    match: string;
    replace: string;
    mode?: 'simple' | 'regex';
    caseSensitive?: boolean;
  }
>;

export type CustomModifier = {
  type: string;
  applyTo: HTTPMessageType;
  arguments: Record<string, unknown>;
};

export type TransactionSelector =
  | UrlMatchSelector
  | MethodMatchSelector
  | ContentContainsSelector
  | MatchContentSha256Selector;

export type TransactionModifier =
  | MockResponseModifier
  | ContentReplaceModifier
  | CustomModifier;

export interface TransactionRule {
  id: string;
  name: string;
  description?: string;
  rules: {
    id: string;
    selectors: TransactionSelector[];
    modifiers: TransactionModifier[];
  }[];
}

export type StringComparisonMode =
  | 'exact'
  | 'in'
  | 'suffix'
  | 'prefix'
  | 'regex';

export type SelectorType =
  | 'tag'
  | 'dom_location'
  | 'page_url'
  | 'text_content'
  | 'attribute'
  | 'anchor_href';

interface BaseSelector<
  T extends SelectorType,
  Args extends Record<string, unknown>,
> {
  id: string;
  type: T;
  arguments: Args;
}

export type TagSelector = BaseSelector<
  'tag',
  {
    tagName: keyof HTMLElementTagNameMap;
  }
>;

export type DOMLocationSelector = BaseSelector<
  'dom_location',
  {
    jsPath: string[];
    xpath: string;
  }
>;

export type PageUrlSelector = BaseSelector<
  'page_url',
  {
    url: string;
  }
>;

export type TextContentSelector = BaseSelector<
  'text_content',
  {
    text: string;
    isCaseSensitive: boolean;
    isExactWord: boolean;
  }
>;

export type AttributeSelector = BaseSelector<
  'attribute',
  {
    attributeName: string;
    attributeValue: string;
  }
>;

export type AnchorHrefSelector = BaseSelector<
  'anchor_href',
  {
    url: string;
    mode: StringComparisonMode;
  }
>;

export type SelectorTypeToSelector = {
  tag: TagSelector;
  dom_location: DOMLocationSelector;
  page_url: PageUrlSelector;
  text_content: TextContentSelector;
  attribute: AttributeSelector;
  anchor_href: AnchorHrefSelector;
};

export type Selector = SelectorTypeToSelector[SelectorType];

export type BlockInteractionsBlockingTypes = 'button' | 'link';
export type BlockInteractionsBlockBy = 'title' | 'url';
export type LinkEditTypes = 'button' | 'link';
export type LinkEditBy = 'title' | 'url';

export interface IAIPromptGroup {
  id: string;
  prompt: string;
  pages: string[];
  isGlobal?: boolean;
  name: string;
}
